import { ChevronDown } from 'lucide-react';
import React, { useState } from 'react';

import { COLORS } from './constants';

export interface SubfactorRowProps {
  label: string;
  score: number | null | undefined;
  tooltip?: string;
  urgency?: string;
  nhdScore?: number;
  nhdDetail?: string;
  /** Optional display font for the numeric score only. */
  scoreFontFamily?: string;
  /** Optional size override for the numeric score only. */
  scoreFontSize?: number;
  /** Whether to show the /100 suffix. */
  showScoreDenominator?: boolean;
  /** Label shown when the subfactor has no score. */
  emptyScoreLabel?: string;
  /** Optional content revealed by a caret on the score row. */
  details?: React.ReactNode;
  /** When true, render the NHD chip + detail tooltip even though the
   *  displayed score is already the post-NHD value (no delta visual).
   *  Use this when hauser-eq has applied the NHD impact server-side and
   *  the frontend only needs to label the row as NHD-influenced. */
  nhdInfluenced?: boolean;
}

/** Color thresholds matching dd.hauserhq.com exactly */
const getScoreColor = (score: number): string => {
  if (score >= 86) return "#f3f3f3";
  if (score >= 77) return "#5AE9FF";
  if (score >= 68) return "#FFF981";
  if (score >= 59) return "#FF9A4D";
  return "#DB5282";
};

export const SubfactorRow = ({
  label,
  score,
  tooltip,
  nhdScore,
  nhdDetail,
  nhdInfluenced = false,
  scoreFontFamily,
  scoreFontSize = 13,
  showScoreDenominator = true,
  emptyScoreLabel = 'No Data',
  details,
}: SubfactorRowProps) => {
  const [showTip, setShowTip] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const isNull = score === null || score === undefined;
  const hasNHD = !isNull && nhdScore !== undefined && nhdScore !== score;
  // Whether to render the NHD chip on the label row. Either there's a
  // visible delta (hasNHD) OR the consumer has flagged the score as
  // post-NHD-adjusted server-side.
  const showChip = hasNHD || nhdInfluenced;
  const displayScore = hasNHD && nhdScore !== undefined ? nhdScore : (score ?? 0);
  const pct = isNull ? 0 : Math.max(0, Math.min(100, ((displayScore - 5) / 90) * 100));
  const origPct = isNull ? 0 : Math.max(0, Math.min(100, (((score ?? 0) - 5) / 90) * 100));
  const c = isNull ? COLORS.textDim : getScoreColor(displayScore);
  const hasSupplementalContent = Boolean(tooltip || nhdDetail);
  const isExpandable = Boolean(details || hasSupplementalContent);
  const isExpanded = Boolean((details && showDetails) || (hasSupplementalContent && showTip));

  const displayLabel = label.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
  const toggleRow = () => {
    if (details) setShowDetails((current) => !current);
    if (tooltip || nhdDetail) setShowTip((current) => !current);
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        data-testid={`subfactor-toggle-${label}`}
        onClick={toggleRow}
        onKeyDown={(event) => {
          if ((event.key === 'Enter' || event.key === ' ') && isExpandable) {
            event.preventDefault();
            toggleRow();
          }
        }}
        role={isExpandable ? 'button' : undefined}
        tabIndex={isExpandable ? 0 : undefined}
        aria-expanded={isExpandable ? isExpanded : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 0",
          cursor: tooltip || nhdDetail || details ? "pointer" : "default",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--tx-text)' }}>{displayLabel}</span>
            {showChip && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)' }}>NHD</span>}
          </div>
          <div
            style={{
              // a hairline track, the score in the theme's ink; colour is kept for the number when it's a concern
              height: 2,
              background: "var(--tx-fill-2)",
              borderRadius: 1,
              marginTop: 8,
              position: "relative",
            }}
          >
            {hasNHD && (
              <div
                style={{
                  position: "absolute",
                  width: `${origPct}%`,
                  height: "100%",
                  background: "var(--tx-fill-3)",
                  borderRadius: 1,
                }}
              />
            )}
            <div
              style={{
                position: "relative",
                width: `${pct}%`,
                height: "100%",
                background: "var(--tx-muted)",
                borderRadius: 1,
                transition: "width 0.6s ease",
              }}
            />
          </div>
        </div>
        <div style={{ width: 44, textAlign: "right" }}>
          {isNull ? (
            <span
              style={{ fontSize: 10, fontWeight: 500, color: COLORS.textDim, fontStyle: "italic" }}
            >
              {emptyScoreLabel}
            </span>
          ) : (
            <span style={{ fontWeight: 300, letterSpacing: '-0.03em', color: displayScore < 68 ? c : 'var(--tx-text)' }}>
              <span style={{ fontFamily: scoreFontFamily, fontSize: scoreFontSize }}>{displayScore}</span>
              {showScoreDenominator && (
                <span style={{ fontSize: 10, fontWeight: 400, color: COLORS.textDim }}>/100</span>
              )}
            </span>
          )}
        </div>
        {isExpandable && (
          <span
            aria-hidden="true"
            style={{
              color: COLORS.textDim,
              display: 'inline-flex',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}>
            <ChevronDown size={14} />
          </span>
        )}
      </div>
      {showDetails && details}
      {showTip && (tooltip || nhdDetail) && (
        <div
          data-testid={`subfactor-supplemental-${label}`}
          style={{
            padding: "4px 0 8px",
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--tx-muted)',
            lineHeight: 1.55,
          }}
        >
          {tooltip && (
            <div style={{ marginBottom: nhdDetail ? 8 : 0 }}>{tooltip}</div>
          )}
          {nhdDetail && (
            <div
              style={{
                borderTop: tooltip ? `1px solid ${COLORS.border}` : undefined,
                paddingTop: tooltip ? 8 : 0,
              }}
            >
              <span
                style={{ color: COLORS.text, fontWeight: 600 }}
              >
                NHD finding:{" "}
              </span>
              <span>{nhdDetail}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubfactorRow;
